import { Modal } from 'antd';
import { PureComponent, ReactNode } from 'react';
import { PropertyGroupEntity } from '../../../../../api/fetchSymDiaProperties';
import { DiagnosisEntityOrSymptomsEntity } from '../../../../../store/types/prescription';
import { randomString } from '../../../../../store/utils';
import { track } from '../../../../../tracking';
import { SYMPTOMS_KIND, DIAGNOSIS_KIND } from '../../../trackSource';
import FreeText from './FreeText';
import SymDiaPropertyInput from './SymDiaPropertyInput';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';

export const SYM_DIA_PROPERTIES_MODAL = 'modal-container';

type MoreOptionsProps = {
    data?: DiagnosisEntityOrSymptomsEntity;
    closeMe: () => void;
    onSave: (data: DiagnosisEntityOrSymptomsEntity) => void;
    properties?: PropertyGroupEntity[];
    isStaticProperties?: boolean;
    kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND;
};

type MoreOptionsState = {
    data?: DiagnosisEntityOrSymptomsEntity;
};

class MoreOptionsModal extends PureComponent<MoreOptionsProps, MoreOptionsState> {
    state: MoreOptionsState = { data: this.props.data };
    REF_MAP: { [key: number]: EkaFocosableElement } = {};
    containerId = `${SYM_DIA_PROPERTIES_MODAL}-${this.props.kind}-${
        this.props.data?.id
    }-${randomString(2)}`;

    onRemarkChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const input = e.target.value;

        this.state.data &&
            this.setState({
                data: {
                    ...this.state.data,
                    remark: input,
                },
            });
    };

    componentDidUpdate = (prevProps: MoreOptionsProps): void => {
        if (prevProps.data !== this.props.data) {
            this.setState({
                data: this.props.data,
            });

            setTimeout(() => {
                const { data, isStaticProperties, properties } = this.props;
                if (!data) {
                    return;
                }
                if (
                    isStaticProperties ||
                    (properties &&
                        (!data.properties ||
                            !data.properties[properties[2]?.id]?.selection ||
                            data.properties[properties[2]?.id]?.selection?.length === 0))
                )
                    this.REF_MAP && this.REF_MAP[2] && this.REF_MAP?.[2]?.focus();
            }, 500);
        }
    };

    getTitle = (): string => {
        const { data, properties } = this.props;

        if (!data) {
            return '';
        }

        if (!properties || properties.length === 0) {
            return data.name;
        }

        return [
            data.name,
            data &&
                data.properties &&
                Object.keys(data.properties)
                    // get top two properties
                    .filter(
                        (id) => id === (properties[0] || {}).id || id === (properties[1] || {}).id,
                    )
                    .map((pId) =>
                        data.properties?.[pId].selection
                            ?.filter((s) => s.value)
                            ?.map((s) => `${s.value}${s.unit ? ` ${s.unit}` : ''}`)
                            .join(' | '),
                    )
                    .filter(Boolean)
                    .join(' | '),
        ]
            .filter(Boolean)
            .join(' | ');
    };

    onUpdate = (data: DiagnosisEntityOrSymptomsEntity, elIndex: number): void => {
        this.setState((prevState) => ({
            data: {
                ...prevState.data!,
                properties: {
                    ...prevState.data?.properties,
                    ...data.properties,
                },
            },
        }));

        // ⬇️ safe blur/focus handling
        const type = this.props.properties?.[elIndex]?.type;
        if (type === 'multi-select-suggest') return;

        const propertyId = this.props.properties?.[elIndex]?.id;
        if (!propertyId) return;

        const selection = data.properties?.[propertyId]?.selection;
        if (!selection || selection.length === 0) return;

        try {
            const currentRef = this.REF_MAP[elIndex];
            const nextRef = this.REF_MAP[elIndex + 1];

            if (currentRef && typeof currentRef.blur === 'function') {
                currentRef.blur();
            }

            if (nextRef && typeof nextRef.focus === 'function') {
                nextRef.focus();
            }
        } catch (e) {
            console.log(e);
        }
    };

    setRef = (ref: EkaFocosableElement, elIndex: number): void => {
        if (!this.REF_MAP) {
            return;
        }

        this.REF_MAP[elIndex] = ref;
        return;
    };

    onInputBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
        track('PX_ELEMENT_PROPERTY', {
            // eId: data.id,
            eName: this.props.data?.name || '',
            // pId: p.id,
            pName: 'Details',
            isStaticP: 'true',
            action: e.target.value.length > 0 ? 'SET' : 'UNSET',
            kind: this.props.kind,
        });
    };

    render(): ReactNode {
        const { closeMe, isStaticProperties, properties, onSave, kind } = this.props;
        const { data } = this.state;

        const leftOverProperties: PropertyGroupEntity[] =
            (data &&
                data.properties &&
                Object.keys(data.properties)
                    .filter((k) => !properties?.find((p) => p.id === k))
                    .map(generateLeftOverProperty(data))) ||
            [];

        return (
            <Modal
                bodyStyle={{
                    overflowY: 'auto',
                    flex: '1 1 auto',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '0px',
                }}
                okButtonProps={{ className: 'primary-btn font-lato' }}
                cancelButtonProps={{ className: 'neutral-btn font-lato' }}
                className="flex flex-col h-3/4"
                title={<div className="font-lato w-full font-700">{this.getTitle()}</div>}
                visible={this.props.data !== undefined}
                onOk={() => {
                    data && onSave(data);
                    closeMe();
                }}
                okText="Save"
                onCancel={() => closeMe()}
                destroyOnClose
            >
                <div id={this.containerId} className="p-16 relative space-y-16 font-lato">
                    {!isStaticProperties &&
                        data &&
                        properties?.map((p, index) =>
                            index > 1 ? (
                                <SymDiaPropertyInput
                                    containerId={this.containerId}
                                    key={p.id}
                                    setRef={(ref) => {
                                        this.setRef(ref, index);
                                    }}
                                    data={data}
                                    p={p}
                                    update={(data) => this.onUpdate(data, index)}
                                    withLabel
                                    kind={kind}
                                />
                            ) : null,
                        )}

                    {data &&
                        leftOverProperties.map((leftOverProperty, index) => (
                            <SymDiaPropertyInput
                                containerId={this.containerId}
                                key={leftOverProperty.id}
                                setRef={(ref) => {
                                    this.setRef(ref, (properties || []).length + index);
                                }}
                                data={data}
                                p={leftOverProperty}
                                update={(data) => this.onUpdate(data, index)}
                                withLabel
                                kind={kind}
                            />
                        ))}

                    {isStaticProperties && (
                        <FreeText
                            setRef={(ref) => {
                                this.setRef(ref, 2);
                            }}
                            placeholder="(e.g. Enter any specific details here)"
                            label="Details"
                            value={data?.remark || ''}
                            onChange={this.onRemarkChange}
                            onBlur={this.onInputBlur}
                        />
                    )}
                </div>
            </Modal>
        );
    }
}
export default MoreOptionsModal;

export function generateLeftOverProperty(
    data: DiagnosisEntityOrSymptomsEntity,
): (
    value: string,
    index: number,
    array: string[],
) => {
    name: string;
    id: string;
    type: 'multi-select-suggest';
    properties: { id: string; name: string }[] | undefined;
} {
    return (leftOverPgKey) => ({
        name: data.properties ? data.properties[leftOverPgKey].name : '',
        id: leftOverPgKey,
        type: 'multi-select-suggest',
        properties: data.properties
            ? data.properties[leftOverPgKey].selection?.map((s) => ({
                  id: s.id,
                  name: s.value,
              }))
            : [],
    });
}
